import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalLogisticsApi from "../services/portalLogisticsApi";
import "../styles/PortalBusiness.css";

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function badgeClass(status) {
  if (!status) return "ucx-portal-badge";
  const s = String(status).toLowerCase();
  if (s === "approved" || s === "delivered") return "ucx-portal-badge ucx-portal-badge-success";
  if (s === "pending") return "ucx-portal-badge ucx-portal-badge-warn";
  if (s === "rejected" || s === "cancelled") return "ucx-portal-badge ucx-portal-badge-danger";
  return "ucx-portal-badge";
}

export default function PortalShipmentsPage() {
  const navigate = useNavigate();

  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "POS Terminals", to: "/portal/pos/terminals", perm: "pos.terminal.read" },
      { label: "POS Inventory", to: "/portal/pos-inventory", perm: "pos.inventory.submit.read" },
      { label: "Accounting", to: "/portal/accounting", perm: "accounting.journals.read" },
      { label: "Purchasing", to: "/portal/purchasing", perm: "purchasing.po.read" },
      { label: "Shipments", to: "/portal/shipments", perm: "logistics.shipments.read" },
    ],
    []
  );

  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line
  const [error, setError] = useState("");

  const [branches, setBranches] = useState([]);
  const [refsError, setRefsError] = useState("");
  const [originLocations, setOriginLocations] = useState([]);
  const [destinationLocations, setDestinationLocations] = useState([]);
  const [transitLocations, setTransitLocations] = useState([]);

  // Filters: keep empty by default
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [filterOriginBranchId, setFilterOriginBranchId] = useState("");
  const [filterDestinationBranchId, setFilterDestinationBranchId] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Create shipment form
  const [showCreate, setShowCreate] = useState(false);
  const [originBranchId, setOriginBranchId] = useState("");
  const [destinationBranchId, setDestinationBranchId] = useState("");
  const [originLocationId, setOriginLocationId] = useState("");
  const [transitLocationId, setTransitLocationId] = useState("");
  const [destinationLocationId, setDestinationLocationId] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [trackingRef, setTrackingRef] = useState("");
  const [notes, setNotes] = useState("");

  const [items, setItems] = useState([{ productId: "", quantity: 1 }]);

  async function loadRefs() {
    setRefsError("");
    try {
      const b = await portalLogisticsApi.listBranches();
      if (!b?.ok) {
        if (b?.error === "forbidden") {
          setRefsError("Forbidden: you do not have access to branches. Ask admin to grant branch access.");
        } else {
          setRefsError(b?.error || "Unable to load branches.");
        }
        setBranches([]);
        return;
      }
      const list = b.rows || b.branches || [];
      setBranches(list);
    } catch (e) {
      setRefsError(e?.response?.data?.error || "Unable to load branches.");
      setBranches([]);
    }
  }

  async function loadOriginLocations(branchId) {
    if (!branchId) {
      setOriginLocations([]);
      setTransitLocations([]);
      setOriginLocationId("");
      setTransitLocationId("");
      return;
    }
    const res = await portalLogisticsApi.listLocations({ branchId: Number(branchId) });
    if (!res?.ok) {
      setOriginLocations([]);
      setTransitLocations([]);
      return;
    }
    const list = res.rows || res.locations || [];
    setOriginLocations(list);
    // best-effort: pick first as default if none selected
    if (!originLocationId && list[0]) setOriginLocationId(String(list[0].id));
    // best-effort: try to pre-pick a transit location if it exists
    const transit = list.find((x) => String(x.type || "").toLowerCase() === "transit" || String(x.name || "").toLowerCase().includes("transit"));
    setTransitLocations(list);
    if (!transitLocationId && transit) setTransitLocationId(String(transit.id));
  }

  async function loadDestinationLocations(branchId) {
    if (!branchId) {
      setDestinationLocations([]);
      setDestinationLocationId("");
      return;
    }
    const res = await portalLogisticsApi.listLocations({ branchId: Number(branchId) });
    if (!res?.ok) {
      setDestinationLocations([]);
      return;
    }
    const list = res.rows || res.locations || [];
    setDestinationLocations(list);
    if (!destinationLocationId && list[0]) setDestinationLocationId(String(list[0].id));
  }

  async function loadShipments() {
    setLoading(true);
    setError("");
    try {
      const res = await portalLogisticsApi.listShipments({
        q: q || undefined,
        status: status || undefined,
        pendingStatus: pendingStatus || undefined,
        pendingAction: pendingAction || undefined,
        originBranchId: filterOriginBranchId ? Number(filterOriginBranchId) : undefined,
        destinationBranchId: filterDestinationBranchId ? Number(filterDestinationBranchId) : undefined,
        limit: 100,
        offset: 0,
      });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      const list = res.rows || res.shipments || [];
      setRows(list);
      setTotal(Number(res.total || list.length || 0));
    } catch (e) {
      setError(e?.response?.data?.error || "Unable to load shipments");
    } finally {
      setLoading(false);
    }
  }

  async function submitCreate() {
    setLoading(true);
    setError("");
    try {
      const payload = {
        originBranchId: Number(originBranchId),
        destinationBranchId: Number(destinationBranchId),
        originLocationId: Number(originLocationId),
        transitLocationId: transitLocationId ? Number(transitLocationId) : null,
        destinationLocationId: Number(destinationLocationId),
        carrierName: carrierName || null,
        trackingRef: trackingRef || null,
        notes: notes || null,
        items: (items || []).map((it) => ({
          productId: Number(it.productId),
          quantity: Number(it.quantity || 0),
        })),
      };

      const res = await portalLogisticsApi.createShipment(payload);
      if (!res?.ok) throw new Error(res?.error || "create_failed");

      setShowCreate(false);
      setCarrierName("");
      setTrackingRef("");
      setNotes("");
      setItems([{ productId: "", quantity: 1 }]);

      // reload list
      await loadShipments();

      // If backend returns shipment id, go to detail
      const sid = res.shipment?.id || res.id;
      if (sid) navigate(`/portal/shipments/${sid}`);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Unable to create shipment");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // create form location loading, same pattern as admin page
    loadOriginLocations(originBranchId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originBranchId]);

  useEffect(() => {
    loadDestinationLocations(destinationBranchId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationBranchId]);

  useEffect(() => {
    loadRefs();
    loadShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav title="Shipments" subtitle="Create and track shipments (admin approvals enforced)" links={links} />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Shipments</h1>
              <p className="ucx-portal-sub">Create shipment requests, track status, and view timelines.</p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={loadShipments}>
                Reload
              </button>
              <button className="ucx-btn ucx-btn-primary" type="button" onClick={() => setShowCreate((v) => !v)}>
                {showCreate ? "Close Create" : "Create Shipment"}
              </button>
            </div>
          </div>

          {refsError ? <div className="ucx-portal-error">{refsError}</div> : null}
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          <div className="ucx-portal-card-large">
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Filters</div>
              <div className="ucx-portal-badge">Total: {total}</div>
            </div>

            <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              <div className="ucx-portal-field">
                <label>Search</label>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Shipment no, tracking, notes…" />
              </div>

              <div className="ucx-portal-field">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="draft">draft</option>
                  <option value="ready">ready</option>
                  <option value="in_transit">in_transit</option>
                  <option value="delivered">delivered</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>

              <div className="ucx-portal-field">
                <label>Pending Status</label>
                <select value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>

              <div className="ucx-portal-field">
                <label>Pending Action</label>
                <input
                  value={pendingAction}
                  onChange={(e) => setPendingAction(e.target.value)}
                  placeholder="e.g. CREATE / SHIP / ARRIVE…"
                />
              </div>

              <div className="ucx-portal-field">
                <label>Origin Branch</label>
                <select value={filterOriginBranchId} onChange={(e) => setFilterOriginBranchId(e.target.value)}>
                  <option value="">All</option>
                  {(branches || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} (#{b.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-portal-field">
                <label>Destination Branch</label>
                <select value={filterDestinationBranchId} onChange={(e) => setFilterDestinationBranchId(e.target.value)}>
                  <option value="">All</option>
                  {(branches || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} (#{b.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-portal-field" style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
                <button className="ucx-btn ucx-btn-primary" type="button" onClick={loadShipments}>
                  Apply
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  type="button"
                  onClick={() => {
                    setQ("");
                    setStatus("");
                    setPendingStatus("");
                    setPendingAction("");
                    setFilterOriginBranchId("");
                    setFilterDestinationBranchId("");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {showCreate ? (
            <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Create Shipment (request)</div>
                <div className="ucx-portal-toolbar-hint">Submitting creates a pending request for admin approval.</div>
              </div>

              <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                <div className="ucx-portal-field">
                  <label>Origin Branch</label>
                  <select value={originBranchId} onChange={(e) => setOriginBranchId(e.target.value)}>
                    <option value="">Select…</option>
                    {(branches || []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} (#{b.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  
                  <label>Origin Location</label>
                  <select value={originLocationId} onChange={(e) => setOriginLocationId(e.target.value)}>
                    <option value="">Select…</option>
                    {(originLocations || []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} (#{l.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  <label>Transit Location (optional)</label>
                  <select value={transitLocationId} onChange={(e) => setTransitLocationId(e.target.value)}>
                    <option value="">None</option>
                    {(transitLocations || []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} (#{l.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                    <label>Destination Branch</label>
                  <select value={destinationBranchId} onChange={(e) => setDestinationBranchId(e.target.value)}>
                    <option value="">Select…</option>
                    {(branches || []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} (#{b.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  <label>Destination Location</label>
                  <select value={destinationLocationId} onChange={(e) => setDestinationLocationId(e.target.value)}>
                    <option value="">Select…</option>
                    {(destinationLocations || []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} (#{l.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ucx-portal-field">
                  <label>Carrier</label>
                  <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} placeholder="Optional" />
                </div>

                <div className="ucx-portal-field">
                  <label>Tracking Ref</label>
                  <input value={trackingRef} onChange={(e) => setTrackingRef(e.target.value)} placeholder="Optional" />
                </div>

                <div className="ucx-portal-field" style={{ gridColumn: "span 2" }}>
                  <label>Notes</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              <div className="ucx-portal-lineitems" style={{ marginTop: 12 }}>
                <div className="ucx-portal-lineitems-head">
                  <div className="ucx-portal-panel-title">Items</div>
                  <div className="ucx-portal-actions">
                    <button
                      className="ucx-btn ucx-btn-outline"
                      type="button"
                      onClick={() => setItems((arr) => [...arr, { productId: "", quantity: 1 }])}
                    >
                      Add Line
                    </button>
                  </div>
                </div>

                <div className="ucx-portal-tablewrap">
                  <table className="ucx-portal-table" style={{ minWidth: 820 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 180 }}>Product ID</th>
                        <th style={{ width: 160 }}>Quantity</th>
                        <th> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(items || []).map((it, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              className="ucx-portal-input-compact"
                              value={it.productId}
                              onChange={(e) => {
                                const v = e.target.value;
                                setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, productId: v } : x)));
                              }}
                              placeholder="e.g. 12"
                            />
                          </td>
                          <td>
                            <input
                              className="ucx-portal-input-compact"
                              type="number"
                              min="1"
                              value={it.quantity}
                              onChange={(e) => {
                                const v = e.target.value;
                                setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, quantity: v } : x)));
                              }}
                            />
                          </td>
                          <td>
                            <button
                              className="ucx-btn ucx-btn-outline"
                              type="button"
                              onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!items?.length ? (
                        <tr>
                          <td colSpan={3} className="ucx-muted">
                            Add at least one item line.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div className="ucx-portal-toolbar">
                  <div className="ucx-portal-toolbar-hint">
                    Ensure locations belong to the correct branches (backend enforces).
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="ucx-btn ucx-btn-primary" type="button" onClick={submitCreate} disabled={loading}>
                      Submit Create Request
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="ucx-portal-card-large" style={{ marginTop: 14 }}>
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Shipments</div>
              <div className="ucx-portal-toolbar-hint">Click a row to open shipment details.</div>
            </div>

            <div className="ucx-portal-tablewrap">
              <table className="ucx-portal-table" style={{ minWidth: 980 }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Shipment #</th>
                    <th>Status</th>
                    <th>Pending</th>
                    <th>Origin → Destination</th>
                    <th>Tracking</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {(rows || []).length ? (
                    (rows || []).map((s) => {
                      const pending = s.pending_status || s.pendingStatus;
                      const pAction = s.pending_action || s.pendingAction;
                      return (
                        <tr
                          key={s.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(`/portal/shipments/${s.id}`)}
                        >
                          <td>{s.id}</td>
                          <td>{s.shipment_no || s.shipmentNo || "-"}</td>
                          <td>
                            <span className={badgeClass(s.status)}>{s.status || "-"}</span>
                          </td>
                          <td>
                            {pending ? (
                              <span className={badgeClass(pending)}>
                                {pending} {pAction ? `(${pAction})` : ""}
                              </span>
                            ) : (
                              <span className="ucx-muted">—</span>
                            )}
                          </td>
                          <td>
                            #{s.origin_branch_id || s.originBranchId} → #{s.destination_branch_id || s.destinationBranchId}
                          </td>
                          <td>{s.tracking_ref || s.trackingRef || "-"}</td>
                          <td>{fmtDate(s.created_at || s.createdAt)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="ucx-muted">
                        No shipments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
